const TZ = 'Asia/Tehran'

const WEEKDAY_MAP: Record<string, number> = {
  Sat: 0,
  Sun: 1,
  Mon: 2,
  Tue: 3,
  Wed: 4,
  Thu: 5,
  Fri: 6,
}

export type TehranParts = {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  weekday: number
}

function readParts(date: Date): Record<string, string> {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    weekday: 'short',
    hourCycle: 'h23',
  })
  return Object.fromEntries(fmt.formatToParts(date).map((part) => [part.type, part.value]))
}

export function toTehranParts(date: Date): TehranParts {
  const parts = readParts(date)
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    weekday: WEEKDAY_MAP[parts.weekday ?? ''] ?? 0,
  }
}

/** Convert a Tehran-local civil date+time (`YYYY-MM-DD`, `HH:mm`) to a UTC Date. */
export function fromTehranLocal(dateStr: string, timeStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number)
  const [hour, minute] = timeStr.split(':').map(Number)
  if (!year || !month || !day || hour == null || minute == null) {
    throw new Error(`invalid Tehran local datetime: ${dateStr} ${timeStr}`)
  }
  const guess = new Date(Date.UTC(year, month - 1, day, hour, minute, 0))
  const parts = toTehranParts(guess)
  const desired = Date.UTC(year, month - 1, day, hour, minute)
  const actual = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute)
  return new Date(guess.getTime() + (desired - actual))
}

export function tehranWeekday(date: Date): number {
  return toTehranParts(date).weekday
}

export function tehranDateString(date: Date): string {
  const parts = toTehranParts(date)
  return `${String(parts.year).padStart(4, '0')}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`
}

export function tehranTimeString(date: Date): string {
  const parts = toTehranParts(date)
  return `${String(parts.hour).padStart(2, '0')}:${String(parts.minute).padStart(2, '0')}`
}

export function addTehranDays(dateStr: string, days: number): string {
  const noon = fromTehranLocal(dateStr, '12:00')
  noon.setUTCDate(noon.getUTCDate() + days)
  return tehranDateString(noon)
}

export function minutesToTime(total: number): string {
  const wrapped = ((total % (24 * 60)) + (24 * 60)) % (24 * 60)
  const hour = Math.floor(wrapped / 60)
  const minute = wrapped % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export function timeToMinutes(time: string): number {
  const [hour, minute] = time.split(':').map(Number)
  return (hour ?? 0) * 60 + (minute ?? 0)
}

/** Inclusive civil date → exclusive UTC end (00:00 of the next Tehran day). */
export function tehranDayEndExclusive(dateStr: string): Date {
  return fromTehranLocal(addTehranDays(dateStr, 1), '00:00')
}

export function tehranDayStart(dateStr: string): Date {
  return fromTehranLocal(dateStr, '00:00')
}

export function tehranToday(): string {
  return tehranDateString(new Date())
}
